import type { ChangePoint } from "@repo/api";
import {
  activatePerformanceMeasureDefinition,
  activateQuestionnaireDefinition,
  correctPerformanceMeasureDefinition,
  correctQuestionnaireDefinition,
  deactivatePerformanceMeasureDefinition,
  deactivateQuestionnaireDefinition,
  definePerformanceMeasureDefinition,
  defineQuestionnaireDefinition,
  mintQuestionnaireDefinitionUrl,
  type QuestionnaireDefinitionDto,
  revisePerformanceMeasureDefinition,
  reviseQuestionnaireDefinition,
  useQuestionnaireDefinitionHistory,
} from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { useState, useTransition } from "react";
import { toast } from "sonner";
import { QuestionnaireBuilder, useQuestionnaireBuilder } from "@/features/catalog/components/builder/questionnaire";
import { questionnaireSectionsInput } from "@/features/catalog/components/builder/questionnaire/questionnaire-input";
import { definitionToDraft, scoresInput } from "@/features/catalog/components/builder/questionnaire/types";
import { QuestionnaireItemView } from "@/features/catalog/components/views/questionnaire-item-view";
import { QuestionnaireScoresView } from "@/features/catalog/components/views/questionnaire-scores-view";
import { CATALOG_STATUS_OPTIONS } from "@/features/catalog/lib/catalog-status-options";
import { questionnaireDefinitionRouteId } from "@/features/catalog/lib/questionnaire-definition-route";
import { questionnaireSaveProblem, questionnaireShellInput } from "@/features/catalog/lib/questionnaire-save";
import { ReadOnlyAlert } from "@/shared/common/read-only-alert";
import { ScoringProperty } from "../views/scoring-property";
import { DeactivateAction, EditLink, SaveCancelActions } from "./actions";
import { CatalogDefinition, type DefinitionShell, useShellDirty, useShellForm } from "./shell";

/** A questionnaire is either available or it is not — there is no draft to publish from. */
const AVAILABILITY_OPTIONS = CATALOG_STATUS_OPTIONS.filter((o) => o.value === "active" || o.value === "retired");

export function toShell(questionnaire: QuestionnaireDefinitionDto): DefinitionShell {
  return {
    kindLabel: "Questionnaire",
    title: questionnaire.name,
    description: questionnaire.description,
    status: questionnaire.availability === "Active" ? "active" : "retired",
    version: String(questionnaire.version),
    lastUpdated: questionnaire.mintedAt,
  };
}

interface QuestionnaireReadProps {
  questionnaire: QuestionnaireDefinitionDto;
  performance?: boolean;
  canEdit: boolean;
  asOf?: string;
  changePoints?: readonly ChangePoint[];
  onRevised: () => void;
}

export function QuestionnaireRead({
  questionnaire,
  canEdit,
  asOf,
  changePoints,
  onRevised,
  performance = false,
}: QuestionnaireReadProps) {
  const { activate: activateQuestionnaireDefinition, deactivate: deactivateQuestionnaireDefinition } = performance
    ? performanceActions
    : questionnaireActions;
  const [allExpanded, setAllExpanded] = useState<boolean | undefined>(undefined);
  const versionHistory = useQuestionnaireDefinitionHistory(questionnaire.url, performance);
  const items = questionnaire.sections;
  const isRetired = questionnaire.availability === "Inactive";
  const hasGroups = items.some((section) => section.items.length > 0);

  return (
    <CatalogDefinition.Root
      shell={{ ...toShell(questionnaire), ...(performance ? { kindLabel: "Performance measure" } : {}) }}
      canEdit={canEdit}
      versions={versionHistory?.versions ?? []}
      asOf={asOf}
      changePoints={changePoints}
    >
      <CatalogDefinition.Toolbar>
        <EditLink
          canEdit={canEdit}
          to={
            performance
              ? "/catalog/performance-measures/$questionnaireId/edit"
              : "/catalog/questionnaires/$questionnaireId/edit"
          }
          params={{ questionnaireId: questionnaireDefinitionRouteId(questionnaire.url) }}
        />
        <DeactivateAction
          canEdit={canEdit}
          isRetired={isRetired}
          name={questionnaire.name}
          onDeactivate={() => deactivateQuestionnaireDefinition({ definitionUrl: questionnaire.url })}
          onActivate={() => activateQuestionnaireDefinition({ definitionUrl: questionnaire.url })}
          onDeactivated={onRevised}
        />
      </CatalogDefinition.Toolbar>

      <CatalogDefinition.Header>
        <CatalogDefinition.Title />
        <CatalogDefinition.Description />
        <CatalogDefinition.Properties>
          <ScoringProperty enabled={questionnaire.scores.length > 0} disabled />
        </CatalogDefinition.Properties>
      </CatalogDefinition.Header>

      <CatalogDefinition.Body>
        <CatalogDefinition.Content>
          {items.length > 0 && (
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-medium text-muted-foreground">Questions ({items.length})</h2>
                {hasGroups && (
                  <Button variant="ghost" size="sm" onClick={() => setAllExpanded((prev) => !prev)}>
                    {allExpanded ? (
                      <>
                        <ChevronsDownUp className="size-4" />
                        Collapse all
                      </>
                    ) : (
                      <>
                        <ChevronsUpDown className="size-4" />
                        Expand all
                      </>
                    )}
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {items.map((item) => (
                  <QuestionnaireItemView key={item.id} item={item} forceOpen={allExpanded} />
                ))}
              </div>
            </section>
          )}
          <QuestionnaireScoresView questionnaire={questionnaire} />
        </CatalogDefinition.Content>
      </CatalogDefinition.Body>
    </CatalogDefinition.Root>
  );
}

const EMPTY_QUESTIONNAIRE_SHELL: DefinitionShell = {
  kindLabel: "Questionnaire",
  title: "",
  description: "",
  status: "active",
};

interface QuestionnaireEditProps {
  /** Absent on /new. Present on /edit, where it seeds both forms. */
  questionnaire?: QuestionnaireDefinitionDto;
  performance?: boolean;
  canEdit: boolean;
  onDone: (url?: string) => void;
  onCancel: () => void;
}

/**
 * The builder session, serving `/new` and `/edit` alike — create is edit with
 * empty defaults. The save handler lives here rather than in the route: it is
 * the kind's business which intent persists a questionnaire and what an
 * expected revision means.
 */
export function QuestionnaireEdit({
  questionnaire,
  canEdit,
  onDone,
  onCancel,
  performance = false,
}: QuestionnaireEditProps) {
  const {
    correct: correctQuestionnaireDefinition,
    define: defineQuestionnaireDefinition,
    revise: reviseQuestionnaireDefinition,
  } = performance ? performanceActions : questionnaireActions;
  const [isSaving, startSave] = useTransition();
  const builder = useQuestionnaireBuilder(questionnaire ? definitionToDraft(questionnaire) : undefined, performance);

  const shellForm = useShellForm({
    shell: questionnaire
      ? { ...toShell(questionnaire), ...(performance ? { kindLabel: "Performance measure" } : {}) }
      : { ...EMPTY_QUESTIONNAIRE_SHELL, kindLabel: performance ? "Performance measure" : "Questionnaire" },
    values: questionnaire ? { status: questionnaire.availability === "Active" ? "active" : "retired" } : undefined,
    statusOptions: AVAILABILITY_OPTIONS,
  });

  const editHistory = useQuestionnaireDefinitionHistory(questionnaire?.url, performance);
  // Undefined only while creating, so a create session has no history button and
  // an edit gets one immediately rather than after the live query resolves.
  const editVersions = questionnaire ? (editHistory?.versions ?? []) : undefined;

  // The questionnaire builder tracks its own dirty flag in an xstate store.
  const isDirty = useShellDirty(shellForm) || builder.isDirty;

  const save = () =>
    startSave(async () => {
      const shell = questionnaireShellInput(shellForm.form.state.values);
      const sections = questionnaireSectionsInput(builder.draft.sections);
      const { scores, totalBands } = scoresInput(builder.draft, performance);
      const problem = questionnaireSaveProblem({ ...shell, sections, scores, totalBands });
      if (problem) {
        toast.error(problem);
        return;
      }
      try {
        if (questionnaire) {
          if (shell.name !== questionnaire.name || shell.description !== questionnaire.description) {
            await correctQuestionnaireDefinition({
              definitionUrl: questionnaire.url,
              name: shell.name,
              description: shell.description ?? null,
            }).isPersisted.promise;
          }
          // A revision mints the next version, so only a structural edit sends one.
          if (builder.isDirty) {
            await reviseQuestionnaireDefinition({
              definitionUrl: questionnaire.url,
              expectedVersion: questionnaire.version,
              sections,
              scores,
              totalBands,
            }).isPersisted.promise;
          }
        } else {
          const definitionUrl = mintQuestionnaireDefinitionUrl();
          await defineQuestionnaireDefinition({ definitionUrl, ...shell, sections, scores, totalBands }).isPersisted
            .promise;
          builder.actions.markSaved();
          onDone(definitionUrl);
          return;
        }
        builder.actions.markSaved();
        onDone(questionnaire?.url);
      } catch {
        // The shared outbox reports save failures.
      }
    });

  return (
    <CatalogDefinition.EditRoot form={shellForm} canEdit={canEdit} versions={editVersions}>
      <CatalogDefinition.Toolbar>
        <SaveCancelActions
          canEdit={canEdit}
          isDirty={isDirty}
          isSaving={isSaving}
          saveLabel={questionnaire ? "Save" : "Create"}
          onSave={save}
          onCancel={onCancel}
        />
      </CatalogDefinition.Toolbar>

      {!canEdit && (
        <div className="px-6 pt-3">
          <ReadOnlyAlert />
        </div>
      )}

      <CatalogDefinition.Header>
        <CatalogDefinition.Title placeholder={performance ? "Performance measure title" : "Questionnaire title"} />
        <CatalogDefinition.Description placeholder="Add description..." />
        <CatalogDefinition.Properties>
          <CatalogDefinition.StatusPicker />
          <ScoringProperty
            enabled={builder.scoringEnabled}
            onChange={builder.actions.setScoringEnabled}
            disabled={!canEdit || performance}
          />
        </CatalogDefinition.Properties>
      </CatalogDefinition.Header>

      <CatalogDefinition.Body>
        <CatalogDefinition.Content>
          <QuestionnaireBuilder builder={builder} performance={performance} />
        </CatalogDefinition.Content>
      </CatalogDefinition.Body>
    </CatalogDefinition.EditRoot>
  );
}

const questionnaireActions = {
  activate: activateQuestionnaireDefinition,
  deactivate: deactivateQuestionnaireDefinition,
  correct: correctQuestionnaireDefinition,
  define: defineQuestionnaireDefinition,
  revise: reviseQuestionnaireDefinition,
};
const performanceActions = {
  activate: activatePerformanceMeasureDefinition,
  deactivate: deactivatePerformanceMeasureDefinition,
  correct: correctPerformanceMeasureDefinition,
  define: definePerformanceMeasureDefinition,
  revise: revisePerformanceMeasureDefinition,
};
